import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileSignature, Loader2, Save, Plus, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const FIELDS = [
  ['title', 'Document title'],
  ['subtitle', 'Subtitle'],
  ['author_name', 'Author'],
  ['degree', 'Degree / submission line'],
  ['university', 'University'],
  ['department', 'Department / faculty'],
  ['affiliation', 'Affiliation'],
  ['supervisor', 'Supervisor'],
  ['co_supervisor', 'Co-supervisor'],
  ['city', 'City'],
  ['year', 'Year'],
];

export default function FrontMatterPanel({ userId, projectId }) {
  const [fm, setFm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await thesisApi.getFrontMatter(userId, projectId);
      setFm({ ...data, abbreviations: data.abbreviations || [] });
    } catch (e) {
      console.error(e);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setFm((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await thesisApi.saveFrontMatter(userId, projectId, {
        ...FIELDS.reduce((acc, [k]) => ({ ...acc, [k]: fm[k] || '' }), {}),
        declaration_text: fm.declaration_text || '',
        abbreviations: (fm.abbreviations || []).filter((a) => (a.short || '').trim()),
      });
      toast.success('Front matter saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const res = await thesisApi.detectAbbreviations(userId, projectId);
      setFm((s) => ({ ...s, abbreviations: res.abbreviations }));
      toast.success(`Detected ${res.detected.length} abbreviations in your sections`);
    } catch (e) {
      toast.error('Detection failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDetecting(false);
    }
  };

  if (!fm) return null;

  return (
    <Card data-testid="front-matter-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <FileSignature className="w-5 h-5 text-[#1d4ed8]" /> Front matter
          <Badge variant="outline" className="text-[10px] whitespace-normal text-left">title page · TOC · abbreviations · declaration</Badge>
        </CardTitle>
        <CardDescription>
          Used when you enable the structural parts in the Export tab: a formatted title page, an
          automatic table of contents, the list of abbreviations and your declaration of originality.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
              <Input value={fm[key] || ''} onChange={(e) => set(key, e.target.value)}
                data-testid={`fm-${key}-input`} />
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Declaration of originality</div>
          <Textarea rows={4} value={fm.declaration_text || ''}
            onChange={(e) => set('declaration_text', e.target.value)}
            data-testid="fm-declaration-input" />
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              List of abbreviations ({(fm.abbreviations || []).length})
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={handleDetect} disabled={detecting}
                data-testid="fm-detect-abbrev-btn">
                {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                Detect from sections
              </Button>
              <Button size="sm" variant="ghost"
                onClick={() => set('abbreviations', [...(fm.abbreviations || []), { short: '', long: '' }])}
                data-testid="fm-add-abbrev-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="fm-abbrev-list">
            {(fm.abbreviations || []).map((a, i) => (
              <div key={i} className="flex gap-2 items-center min-w-0">
                <Input className="w-20 sm:w-28 shrink-0 font-mono text-xs" value={a.short || ''} placeholder="OS"
                  onChange={(e) => set('abbreviations', fm.abbreviations.map((x, j) => j === i ? { ...x, short: e.target.value } : x))}
                  data-testid={`fm-abbrev-short-${i}`} />
                <Input className="flex-1 min-w-0" value={a.long || ''} placeholder="Overall survival"
                  onChange={(e) => set('abbreviations', fm.abbreviations.map((x, j) => j === i ? { ...x, long: e.target.value } : x))}
                  data-testid={`fm-abbrev-long-${i}`} />
                <Button size="icon" variant="ghost"
                  onClick={() => set('abbreviations', fm.abbreviations.filter((_, j) => j !== i))}
                  data-testid={`fm-abbrev-delete-${i}`}>
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving}
          className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="fm-save-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            : <><Save className="w-4 h-4 mr-2" />Save front matter</>}
        </Button>
      </CardContent>
    </Card>
  );
}
